import { DownOutlined, UploadOutlined } from '@ant-design/icons'
import { useUpload } from '@web/react-common'
import { useRequest } from 'ahooks'
import { Button, Dropdown, Form, Input, Menu, MenuProps, Modal, Select, message } from 'antd'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getPointPermission } from '@/components'
import { PAGE_RESOURCE_POINTS_ENUM, Resource } from '@/schemas'

import { FileListContainer } from './file-list.component'

interface ProjectSummaryContainerProps {
  pp: Resource
}

const CATEGORIES = ['PRD/需求文档', 'HLD/设计文档', '验收报告', '交付物', '其他']

export const PpFileContainer: React.FC<ProjectSummaryContainerProps> = ({ pp }) => {
  const { t } = useTranslation()
  const projectId = pp.selfId
  const { canCreate, canUpdate, canDelete } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.文件, pp.permissions)

  const [visible, setVisible] = useState(false)
  const [type, setType] = useState<number | null>(2)
  const [form] = Form.useForm()
  const [currentFile, setCurrentFile] = useState<any>()

  const { data, run, loading } = useRequest(() => ({
    url: '/api/project/getProjectFiles',
    params: {
      selfId: projectId,
    },
  }))

  const { run: createPpFile } = useRequest(
    (data) => ({
      url: '/api/project/createPpFile',
      method: 'post',
      data,
    }),
    {
      manual: true,
      onSuccess: () => {
        setVisible(false)
        form.resetFields()
        run()
      },
    }
  )

  const { run: uplaod } = useRequest(
    (data) => ({
      url: '/api/file/upload',
      method: 'post',
      data,
    }),
    {
      manual: true,
    }
  )

  const { uploadClickRef } = useUpload(
    {
      upload: async (file) => {
        setCurrentFile(file)
        return await uplaod(file)
      },
      onUploadStart(_file) {},
      onUploadProgress(_file) {},
      onUploadComplete(file) {
        if (file) {
          form.setFieldsValue({ file })
        }
      },
      onUploadError(_file) {},
      clickOptions: {
        accept: '*',
      },
    },
    [visible]
  )

  const onOk = async () => {
    await form.validateFields().then((values) => {
      if (type === 2 && !values.file && !currentFile) {
        message.warning(t('upload error'))
        return
      }
      const params = {
        selfId: projectId,
        type: pp.type,
        category: values.category,
        title: values.title,
        url: values.url,
      }
      if (type === 2) {
        params['url'] = values.file.url
        params['name'] = currentFile.name
        params['fileType'] = currentFile.type
        params['size'] = currentFile.size
        params['uuid'] = values.file.uuid
      }
      createPpFile(params)
    })
  }

  const clickMenu: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation()
    setType(Number(key))
    setVisible(true)
  }

  const menu = (
    <Menu onClick={clickMenu}>
      <Menu.Item key='1'>{t('add link')}</Menu.Item>
      <Menu.Item key='2'>{t('add local')}</Menu.Item>
    </Menu>
  )

  const formDom = (
    <Form layout='vertical' form={form}>
      <Form.Item label={t('file category')} name='category' rules={[{ required: true }]}>
        <Select options={CATEGORIES.map((v) => ({ label: t(v), value: v }))} />
      </Form.Item>
      <Form.Item label={t('file title')} name='title' rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      {type === 1 && (
        <Form.Item label={t('file url')} name='url' rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      )}
      {type === 2 && (
        <Form.Item label={t('upload file')} name='file' rules={[{ required: true }]}>
          <Button ref={uploadClickRef} icon={<UploadOutlined />}>
            {t('upload click')}
          </Button>
        </Form.Item>
      )}
    </Form>
  )

  return (
    <div className={'flex flex-col'}>
      <Dropdown overlay={menu} trigger={['click']} disabled={!canCreate}>
        <Button
          type='ghost'
          className={'text-16px font-500 w-170px'}
          onClick={(e) => e.preventDefault()}
          icon={<DownOutlined />}
        >
          {t('add file')}
        </Button>
      </Dropdown>
      <FileListContainer
        loading={loading}
        selfId={projectId}
        map={data}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onSuccess={run}
        pp={pp}
      />
      <Modal
        forceRender
        title={type === 1 ? t('add link') : t('add local')}
        width='50vw'
        okText={t('save')}
        visible={visible}
        onOk={onOk}
        onCancel={() => setVisible(false)}
      >
        {formDom}
      </Modal>
    </div>
  )
}
